import { CSSProperties } from 'react';

import { maskMoneyWithSmallSymbol } from '@/services/global';

import styles from './MoneyDisplay.module.scss';

type SizeVariant = 'small' | 'medium' | 'large';
type ColorVariant = 'primary' | 'success' | 'danger' | 'warning' | 'muted';

interface MoneyDisplayProps {
  value: number;
  showSymbol?: boolean;
  symbol?: string;
  symbolSize?: string | number; // e.g., '0.8em', '12px', 14
  symbolStyle?: CSSProperties; // Additional styles for the symbol
  numberStyle?: CSSProperties; // Additional styles for the number
  className?: string;
  style?: CSSProperties; // Style for the container
  size?: SizeVariant; // Predefined size variants
  color?: ColorVariant; // Predefined color variants
  bold?: boolean; // Make the number bold
}

export function MoneyDisplay({
  value,
  showSymbol = true,
  symbol,
  symbolSize,
  symbolStyle = {},
  numberStyle = {},
  className = '',
  style = {},
  size = 'medium',
  color,
  bold = false,
}: MoneyDisplayProps) {
  const containerClasses = [
    styles.moneyDisplay,
    size && styles[size],
    color && styles[color],
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const symbolClasses = [styles.symbol].filter(Boolean).join(' ');
  const numberClasses = [styles.number, bold && 'fw-bold'].filter(Boolean).join(' ');

  const finalSymbolStyle = {
    ...(symbolSize && { fontSize: symbolSize }),
    ...symbolStyle,
  };

  const finalNumberStyle = {
    ...(bold && { fontWeight: 600 }),
    ...numberStyle,
  };

  if (!showSymbol) {
    const formatted = maskMoneyWithSmallSymbol(value, false);
    return (
      <span className={containerClasses} style={style}>
        <span className={numberClasses} style={finalNumberStyle}>
          {formatted}
        </span>
      </span>
    );
  }

  const result = maskMoneyWithSmallSymbol(value, true, symbol);

  if (typeof result === 'string') {
    return (
      <span className={containerClasses} style={style}>
        {result}
      </span>
    );
  }

  return (
    <span className={containerClasses} style={style}>
      <span className={symbolClasses} style={finalSymbolStyle}>
        {result.symbol}
      </span>{' '}
      <span className={numberClasses} style={finalNumberStyle}>
        {result.number}
      </span>
    </span>
  );
}

export default MoneyDisplay;
